/**
 * PacienteHeader — Encabezado clínico fijo del paciente durante la atención.
 * Muestra: nombre completo, edad, sexo, ID, número de orden, tipo de servicio y estado.
 *
 * Requirements: M4.15.1, Issue #84, Issue #87
 */

import { useTranslation } from "react-i18next";
import { User } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { TipoServicioBadge } from "@/components/tipo-servicio-badge";
import type { AtencionDetalle } from "@/hooks/use-muestras";

interface PacienteHeaderProps {
  atencion: AtencionDetalle;
}

export function PacienteHeader({ atencion }: PacienteHeaderProps) {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-between flex-wrap gap-2">
      <div className="flex items-center gap-3">
        <div className="flex items-center justify-center h-10 w-10 rounded-full bg-primary/10 shrink-0">
          <User className="h-5 w-5 text-primary" />
        </div>
        <div>
          <p className="font-semibold text-base leading-tight">{atencion.paciente_nombre}</p>
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground flex-wrap">
            <span>{atencion.paciente_edad}</span>
            <span>·</span>
            <span>
              {atencion.paciente_sexo === "M" ? t("common.masculino") : t("common.femenino")}
            </span>
            <span>·</span>
            <span className="font-mono">{atencion.paciente_documento}</span>
            <span>·</span>
            <span className="font-mono">{atencion.orden_numero}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <TipoServicioBadge tipo={atencion.tipo_servicio} />
        <Badge variant={atencion.estado === "ABIERTA" ? "default" : "secondary"}>
          {atencion.estado_display}
        </Badge>
      </div>
    </div>
  );
}
